/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateMessageRequest } from '../models/create-message-request';
import { GenericResponse } from '../models/generic-response';
import { GetMessageResponse } from '../models/get-message-response';
import { MessageRecipientsResponse } from '../models/message-recipients-response';
import { MessageResponse } from '../models/message-response';

/**
 * Message notification APIs
 */
@Injectable({ providedIn: 'root' })
export class MessageService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllMessages()` */
  static readonly GetAllMessagesPath = '/api/v1/messages';

  /**
   * Get list of messages.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllMessages()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllMessages$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       *  - name
       *  - mobile number
       */
      search?: string;

      /**
       * this adds a filter to the following field:
       *   - senderId
       */
      senderId?: number;

      /**
       * this adds a filter to the following field:
       * - user group
       */
      userGroupId?: Array<number>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetMessageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MessageService.GetAllMessagesPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('senderId', params.senderId, {});
      rb.query('userGroupId', params.userGroupId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetMessageResponse>;
        })
      );
  }

  /**
   * Get list of messages.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllMessages$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllMessages(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       *  - name
       *  - mobile number
       */
      search?: string;

      /**
       * this adds a filter to the following field:
       *   - senderId
       */
      senderId?: number;

      /**
       * this adds a filter to the following field:
       * - user group
       */
      userGroupId?: Array<number>;
    },
    context?: HttpContext
  ): Observable<GetMessageResponse> {
    return this.getAllMessages$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetMessageResponse>): GetMessageResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `sendMessage()` */
  static readonly SendMessagePath = '/api/v1/messages';

  /**
   * send new message.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `sendMessage()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendMessage$Response(
    params: {
      body: CreateMessageRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MessageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MessageService.SendMessagePath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MessageResponse>;
        })
      );
  }

  /**
   * send new message.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `sendMessage$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendMessage(
    params: {
      body: CreateMessageRequest;
    },
    context?: HttpContext
  ): Observable<MessageResponse> {
    return this.sendMessage$Response(params, context).pipe(
      map((r: StrictHttpResponse<MessageResponse>): MessageResponse => r.body)
    );
  }

  /** Path part for operation `getMessageById()` */
  static readonly GetMessageByIdPath = '/api/v1/messages/{id}';

  /**
   * Get message details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMessageById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMessageById$Response(
    params: {
      /**
       * The message's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MessageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MessageService.GetMessageByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MessageResponse>;
        })
      );
  }

  /**
   * Get message details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMessageById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMessageById(
    params: {
      /**
       * The message's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<MessageResponse> {
    return this.getMessageById$Response(params, context).pipe(
      map((r: StrictHttpResponse<MessageResponse>): MessageResponse => r.body)
    );
  }

  /** Path part for operation `disableMessage()` */
  static readonly DisableMessagePath = '/api/v1/messages/{id}';

  /**
   * disable message.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `disableMessage()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableMessage$Response(
    params: {
      /**
       * The message's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MessageService.DisableMessagePath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * disable message.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `disableMessage$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableMessage(
    params: {
      /**
       * The message's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.disableMessage$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `resendMessage()` */
  static readonly ResendMessagePath = '/api/v1/messages/{id}/resend';

  /**
   * Resend the message.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `resendMessage()` instead.
   *
   * This method doesn't expect any request body.
   */
  resendMessage$Response(
    params: {
      /**
       * The message's id that needs to resend
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MessageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MessageService.ResendMessagePath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MessageResponse>;
        })
      );
  }

  /**
   * Resend the message.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `resendMessage$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  resendMessage(
    params: {
      /**
       * The message's id that needs to resend
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<MessageResponse> {
    return this.resendMessage$Response(params, context).pipe(
      map((r: StrictHttpResponse<MessageResponse>): MessageResponse => r.body)
    );
  }

  /** Path part for operation `getMessageRecipientsById()` */
  static readonly GetMessageRecipientsByIdPath =
    '/api/v1/messages/{id}/recipients';

  /**
   * Get message recipients details using message id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMessageRecipientsById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMessageRecipientsById$Response(
    params: {
      /**
       * The message's id that needs to be fetch recipients details
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MessageRecipientsResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MessageService.GetMessageRecipientsByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MessageRecipientsResponse>;
        })
      );
  }

  /**
   * Get message recipients details using message id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMessageRecipientsById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMessageRecipientsById(
    params: {
      /**
       * The message's id that needs to be fetch recipients details
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<MessageRecipientsResponse> {
    return this.getMessageRecipientsById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<MessageRecipientsResponse>
        ): MessageRecipientsResponse => r.body
      )
    );
  }

  /** Path part for operation `sendSmsWithApiKey()` */
  static readonly SendSmsWithApiKeyPath = '/api/v1/messages/send';

  /**
   * Send an SMS message.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `sendSmsWithApiKey()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendSmsWithApiKey$Response(
    params: {
      /**
       * The API key for authentication
       */
      apiKey: string;
      body: CreateMessageRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<MessageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      MessageService.SendSmsWithApiKeyPath,
      'post'
    );
    if (params) {
      rb.header('apiKey', params.apiKey, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<MessageResponse>;
        })
      );
  }

  /**
   * Send an SMS message.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `sendSmsWithApiKey$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendSmsWithApiKey(
    params: {
      /**
       * The API key for authentication
       */
      apiKey: string;
      body: CreateMessageRequest;
    },
    context?: HttpContext
  ): Observable<MessageResponse> {
    return this.sendSmsWithApiKey$Response(params, context).pipe(
      map((r: StrictHttpResponse<MessageResponse>): MessageResponse => r.body)
    );
  }
}
